// Client-level Expenses — read-only view across all matters
// No create — that lives at the matter level

import React from 'react';
import type { Expense } from '../../../../billing/types/billing.types';
import { EXPENSE_CATEGORY_LABELS } from '../../../../billing/types/billing.types';

interface ClientExpensesSectionProps {
  expenses: Expense[];
  loading: boolean;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const SkeletonRow: React.FC = () => (
  <tr>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-24" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-40" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2"><div className="lex-skeleton h-4 w-20" /></td>
    <td className="px-3 py-2 text-right"><div className="lex-skeleton h-4 w-16 ml-auto" /></td>
    <td className="px-3 py-2 text-center"><div className="lex-skeleton h-5 w-16 mx-auto rounded-full" /></td>
  </tr>
);

const ClientExpensesSection: React.FC<ClientExpensesSectionProps> = ({ expenses, loading }) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Expenses
          {!loading && (
            <span className="text-sm font-normal text-gray-400 ml-2">({expenses.length})</span>
          )}
        </h4>
        <p className="text-xs text-gray-400 dark:text-gray-500">Add expenses from individual matter pages</p>
      </div>

      {loading ? (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Category</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Vendor</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
            </tbody>
          </table>
        </div>
      ) : expenses.length === 0 ? (
        <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
          No expenses for this client yet.
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Date</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Matter</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Description</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Category</th>
                <th className="px-3 py-2 text-left text-gray-600 dark:text-gray-300 font-medium">Vendor</th>
                <th className="px-3 py-2 text-right text-gray-600 dark:text-gray-300 font-medium">Amount</th>
                <th className="px-3 py-2 text-center text-gray-600 dark:text-gray-300 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {expenses.map((expense, i) => (
                <tr key={expense.expense_id} className="lex-list-row hover:bg-gray-50 dark:hover:bg-gray-800/50" style={{ '--row-index': i } as React.CSSProperties}>
                  <td className="px-3 py-2 text-gray-900 dark:text-white whitespace-nowrap">{formatDate(expense.expense_date)}</td>
                  <td className="px-3 py-2 text-lex-accent whitespace-nowrap text-xs">
                    {(expense as any).matter?.description || (expense as any).matter?.matter_number || (expense.matter_id ? expense.matter_id.slice(0, 8) : '-')}
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-white max-w-xs truncate">
                    <div className="flex items-center gap-2">
                      <span className="truncate">{expense.description}</span>
                      {expense.ai_categorized && (
                        <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium bg-lex-accent-soft text-lex-accent rounded">AI</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">
                    {EXPENSE_CATEGORY_LABELS[expense.expense_category] || expense.expense_category}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{expense.vendor || '-'}</td>
                  <td className="px-3 py-2 text-right text-gray-900 dark:text-white font-medium tabular-nums whitespace-nowrap">
                    {formatCurrency(Number(expense.total_amount))}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {expense.is_billed ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300">Billed</span>
                    ) : expense.is_non_billable ? (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">Non-billable</span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">Unbilled</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientExpensesSection;
